/**
 * One lead, told to Meta twice: by the browser pixel and by the portal's server-side
 * Conversions API. Both carry the same event id, so Meta counts the pair once, and the server
 * half survives ad blockers (ME-4822).
 */

/** The form field that carries the id from the browser to the server action. */
export const META_EVENT_ID_FIELD = 'meta_event_id';

/** What the portal needs to send the server half from the visitor's own browser. */
export interface MetaLeadContext {
  event_id: string;
  client_ip?: string;
  client_user_agent?: string;
  event_source_url?: string;
  fbp?: string;
  fbc?: string;
}

/**
 * Never throws: tracking must not block a lead. Older Safari and plain-http pages have no
 * randomUUID; without any crypto the id is empty, and then neither half is sent.
 */
export function newMetaEventId(): string {
  try {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    return Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return '';
  }
}

const EVENT_ID = /^[A-Za-z0-9-]{8,64}$/;

/**
 * Built in the server action from the visitor's request: the portal webhook comes from our
 * server, so without this Meta would get our server's IP and browser. Undefined without a valid
 * id, and then the portal sends nothing rather than an unpaired event.
 */
export function metaLeadContext(
  eventId: unknown,
  headers: Pick<Headers, 'get'>,
): MetaLeadContext | undefined {
  if (typeof eventId !== 'string' || !EVENT_ID.test(eventId)) return undefined;

  const ip = (headers.get('x-forwarded-for') ?? headers.get('x-real-ip') ?? '').split(',')[0].trim();
  const referer = headers.get('referer');
  const cookies = headers.get('cookie') ?? '';

  return {
    event_id: eventId,
    client_ip: ip || undefined,
    client_user_agent: headers.get('user-agent') || undefined,
    // Path only: a query string (utm tags, click ids) is not Meta's business here.
    event_source_url: referer ? referer.split('?')[0].split('#')[0] : undefined,
    // Read at submission: the page's attribution snapshot is taken before the pixel sets
    // _fbp on a first visit, so a visitor who books straight away would otherwise send none.
    fbp: cookieValue(cookies, '_fbp'),
    fbc: cookieValue(cookies, '_fbc'),
  };
}

function cookieValue(cookies: string, name: string): string | undefined {
  const match = cookies.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
  if (!match?.[1]) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    // A tampered or foreign cookie must never cost a lead.
    return undefined;
  }
}

/** Meta's advanced matching keys, before hashing. */
export interface MetaMatchKeys {
  em?: string;
  ph?: string;
  fn?: string;
  ln?: string;
  country?: string;
}

/**
 * Normalised as Meta's own Business SDK does on the server half, so both halves hash the same:
 * email and names trimmed and lowercased (no Gmail dot stripping, that is Google's rule), phone
 * as digits with the country code, country as two lowercase letters. First word is the first
 * name, the rest the last name.
 */
export function metaMatchKeys(
  email: string | undefined,
  phoneE164: string | undefined,
  fullName?: string,
  country?: string,
): MetaMatchKeys {
  const [first, ...rest] = (fullName ?? '').trim().split(/\s+/).filter(Boolean);
  return {
    em: email?.trim().toLowerCase() || undefined,
    ph: phoneE164?.replace(/\D/g, '') || undefined,
    fn: first?.toLowerCase() || undefined,
    ln: rest.join(' ').toLowerCase() || undefined,
    country: country?.replace(/[^a-z]/gi, '').toLowerCase() || undefined,
  };
}
